import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert, Box } from "@mui/material";
import { Skeleton } from "../../shared/components/common/SkeletonLoaders/Skeleton";
import { FullPageSkeleton } from "../../shared/components/common/SkeletonLoaders/FullPageSkeleton";
import { setAuthTokenForAllServices } from "../../api/apiServices";
import { setMemoryToken } from "../../auth/utils";
import { setUserContext, trackException, trackTrace } from "../../shared/utils/telemetry";
import { useUserStore } from "../store/useUserStore";
import { isTravellerRole } from "../../shared/utils/roleUtils";
import { normalizeRole, UserRole } from "../../types/user";
import { SkyporterEvents } from "../../analytics/skyporterEvents";
import { UserType } from "../../analytics/dataLayer.types";
import { useGetDefaultRolesQuery } from "../../api/userApi2";
import { useGetUserProfileQuery, useGetWeightUnitsQuery } from "../../api/userApi3";
import { requestLocationPermission, saveCoordinates } from "../../shared/utils/locationService";
import { useToastStore } from "../store/useToastStore";
import { useTranslation } from "react-i18next";
import { useMissedNotifications } from "../../shared/hooks/useMissedNotifications";
import { getReAuthFlag, clearReAuthFlag } from "../../shared/utils/reAuthFlag";
import { performLogoutCleanup, registerAuth0Logout, GLOBAL_LOGOUT_KEY } from "../../shared/utils/logoutCleanup";

interface AuthInitializerProps {
    children: React.ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
    const {
        isLoading,
        error,
        user,
        isAuthenticated,
        getAccessTokenSilently,
        logout: auth0Logout
    } = useAuth0();

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToastStore();

    // Zustand store
    const {
        setUser: setZustandUser,
        logout: zustandLogout,
        setProfileLoading,
        isProfileLoading,
        isAuthenticated: isZustandAuthenticated,
        setDefaultRoles,
        setWeightUnits,
    } = useUserStore();

    const [tokenReady, setTokenReady] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [roleProcessed, setRoleProcessed] = useState(false);
    const [profileCompletenessChecked, setProfileCompletenessChecked] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "success" | "info">("error");

    // TanStack Query hooks
    const {
        data: profileData,
        error: profileError,
        isFetching,
        isLoading: isProfileQueryLoading,
        isSuccess,
        isError,
        refetch,
    } = useGetUserProfileQuery(user?.sub || "", {
        enabled: isAuthenticated && !!user?.sub && tokenReady,
        staleTime: 5000, // Data considered fresh for 5 seconds
        refetchInterval: false, // Don't auto-refetch in background
    });


    // Fetch default roles (public endpoint, always enabled)
    const { data: defaultRolesData } = useGetDefaultRolesQuery();

    // Store default roles in Zustand so role-switch logic can look up string IDs
    useEffect(() => {
        if (defaultRolesData?.roles?.length) {
            setDefaultRoles(defaultRolesData.roles);
        }
    }, [defaultRolesData, setDefaultRoles]);

    // Fetch supported weight units + lb conversion multipliers (requires auth despite backend doc claims)
    const { data: weightUnitsData } = useGetWeightUnitsQuery({
        enabled: isAuthenticated && tokenReady,
    });

    // Store weight units in Zustand so weight conversion/selectors can read them without refetching
    useEffect(() => {
        if (weightUnitsData?.units?.length) {
            setWeightUnits(
                weightUnitsData.units.map((unit) => ({
                    code: unit.unit_code,
                    multiplierFromLb: parseFloat(unit.multiplier_from_lb),
                }))
            );
        }
    }, [weightUnitsData, setWeightUnits]);

    // Fetch missed notifications once when user logs in
    useMissedNotifications();

    // Register Auth0 logout so non-hook contexts (e.g. SignalR) can trigger a full logout
    useEffect(() => {
        registerAuth0Logout(auth0Logout);
    }, [auth0Logout]);

    // Clear re-authentication state on page load (but not on component remount)
    useEffect(() => {
        clearReAuthFlag();
    }, []); // Empty dependency array ensures this runs only once on mount

    // Listen for logout broadcast from other tabs (e.g. after password change)
    useEffect(() => {
        const handleStorageEvent = (event: StorageEvent) => {
            if (event.key === GLOBAL_LOGOUT_KEY && isAuthenticated) {
                performLogoutCleanup();
                auth0Logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                        federated: true,
                    },
                });
            }
        };

        window.addEventListener('storage', handleStorageEvent);
        return () => {
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [isAuthenticated, auth0Logout]);

    // Handle blocked user error from Auth0 redirect
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (errorParam === 'unauthorized' && errorDescription?.toLowerCase().includes('user is blocked')) {
            // Clear state
            zustandLogout();
            setAuthTokenForAllServices(null);
            setTokenReady(false);
            setProfileLoading(false);

            // Store flag so home page can show StatusMessage without a toast round-trip
            try {
                sessionStorage.setItem('auth_blocked_error', 'true');
            } catch {
                // sessionStorage unavailable — silently ignore
            }

            // Go home immediately, clean URL
            navigate('/', { replace: true });
            window.history.replaceState({}, '', '/');
        }
    }, [auth0Logout, zustandLogout, setProfileLoading, showToast, navigate, t]);

    // Handle logout query parameter from failed re-authentication
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('logout') === 'true') {
            window.history.replaceState({}, '', window.location.pathname);

            zustandLogout();
            setAuthTokenForAllServices(null);
            setTokenReady(false);
            setProfileLoading(false);

            auth0Logout({
                logoutParams: {
                    returnTo: window.location.origin,
                    federated: true,
                },
            });
        }
    }, [auth0Logout, zustandLogout, setProfileLoading]);

    useEffect(() => {
        let mounted = true;

        const isMissingRefreshTokenError = (err: unknown): boolean => {
            const message = err instanceof Error ? err.message : String(err);
            return message.includes("Missing Refresh Token");
        };

        const setToken = async () => {
            if (!isAuthenticated) {
                if (getReAuthFlag()) {
                    return;
                }
                setMemoryToken(null);
                setAuthTokenForAllServices(null);
                setTokenReady(false);
                setProfileLoading(false);
                return;
            }

            const MAX_RETRIES = 5;
            const RETRY_DELAY_MS = 500;

            for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                try {
                    const token = await getAccessTokenSilently({
                        authorizationParams: {
                            audience: import.meta.env.VITE_AUTH0_AUDIENCE || "https://skyporter.us/.default",
                            scope: "openid profile email",
                        },
                        cacheMode: 'on'
                    });

                    if (!mounted) return;

                    setMemoryToken(token);
                    setAuthTokenForAllServices(token);
                    setTokenReady(true);
                    return;
                } catch (err) {
                    if (!mounted) return;

                    if (isMissingRefreshTokenError(err) && attempt < MAX_RETRIES) {
                        console.warn(`[AuthInitializer] Token fetch attempt ${attempt} failed (refresh token not ready yet), retrying in ${RETRY_DELAY_MS}ms...`);
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
                        continue;
                    }

                    console.error(`[AuthInitializer] Token fetch FAILED${attempt > 1 ? ` (after ${attempt} attempts)` : ''}`, err);
                    trackException(err as Error, "AuthInitializer: Token Fetch");

                    setSnackbarMessage("Authentication failed. Please try logging in again.");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);

                    setMemoryToken(null);
                    setAuthTokenForAllServices(null);
                    setTokenReady(false);
                    setProfileLoading(false);

                    setTimeout(() => {
                        zustandLogout();
                        auth0Logout({
                            logoutParams: {
                                returnTo: window.location.origin,
                                federated: true,
                            },
                        });
                    }, 3000);
                    return;
                }
            }
        };

        setToken();

        return () => {
            mounted = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.sub, zustandLogout, auth0Logout]);

    // Listen for token:refresh-needed events (fired by tokenInterceptor on 401)
    // and silently refresh the token so the user stays logged in
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleTokenRefreshNeeded = async () => {
            const tryRefresh = async () => {
                const token = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: import.meta.env.VITE_AUTH0_AUDIENCE || "https://skyporter.us/.default",
                        scope: "openid profile email",
                    },
                    cacheMode: 'off', // Force a fresh token, bypass cache
                });
                return token;
            };

            try {
                const token = await tryRefresh();
                console.log("[AuthInitializer] REFRESH SUCCESS (attempt 1)");
                setMemoryToken(token);
                setAuthTokenForAllServices(token);
                window.dispatchEvent(new Event('token:refreshed'));
            } catch (firstErr) {
                console.error("[AuthInitializer] REFRESH FAILED (attempt 1)", firstErr);

                // Retry once before giving up
                try {
                    const token = await tryRefresh();
                    console.log("[AuthInitializer] REFRESH SUCCESS (attempt 2)");
                    setMemoryToken(token);
                    setAuthTokenForAllServices(token);
                    window.dispatchEvent(new Event('token:refreshed'));
                } catch (secondErr) {
                    console.error("[AuthInitializer] REFRESH FAILED (attempt 2) — logging out", secondErr);
                    trackException(secondErr as Error, "AuthInitializer: Silent Token Refresh");
                    zustandLogout();
                    setAuthTokenForAllServices(null);
                    setMemoryToken(null);
                    setTokenReady(false);
                    auth0Logout({
                        logoutParams: {
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                }
            }
        };

        window.addEventListener('token:refresh-needed', handleTokenRefreshNeeded);
        return () => {
            window.removeEventListener('token:refresh-needed', handleTokenRefreshNeeded);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Handle initialization state
    useEffect(() => {
        if (!isLoading) {
            setIsInitializing(false);
        }
        if (!isLoading && !isAuthenticated && isZustandAuthenticated) {
            if (getReAuthFlag()) {
                return;
            }
            zustandLogout();
            setMemoryToken(null);
            setAuthTokenForAllServices(null);
            setTokenReady(false);
            setProfileLoading(false);
            setProfileCompletenessChecked(false);
        }
    }, [isLoading, isAuthenticated, isZustandAuthenticated, zustandLogout]);

    // Check if profile is complete
    const isProfileComplete = (profile: any): boolean => {
        if (!profile) return false;

        // Required fields: firstName, lastName, email, and home address (street)
        const hasRequiredFields =
            profile.full_name &&
            profile.full_name.trim().split(' ').length >= 2 && // At least first and last name
            profile.email &&
            profile.address?.street_no_one;

        return !!hasRequiredFields;
    };

    // Handle profile fetch errors
    useEffect(() => {
        if (isError && profileError) {
            const errorStatus = (profileError as any)?.status;

            if (errorStatus === 404) {
                // Don't redirect yet — let the user pick a role on the home page first.
                // The role selection dialog in AuthNavbar handles new users with no role.
                setProfileLoading(false);
            } else if (errorStatus === 401 || errorStatus === 403) {
                trackException(new Error("Authentication error"), "AuthInitializer: Profile Auth Error");

                setSnackbarMessage("Session expired. Please log in again.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);

                setTimeout(() => {
                    zustandLogout();
                    setAuthTokenForAllServices(null);
                    setTokenReady(false);
                    setProfileLoading(false);

                    auth0Logout({
                        logoutParams: {
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                }, 3000);
            } else if (errorStatus !== undefined && errorStatus >= 500 && errorStatus < 600) {
                trackException(new Error("Server error fetching profile"), "AuthInitializer: Profile Server Error");

                setSnackbarMessage("Server error. Retrying...");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);

                setTimeout(() => {
                    refetch();
                }, 3000);
            } else {
                // Network error (no status code) — e.g. no internet connection
                setSnackbarMessage("No internet connection. Logging out...");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);

                setTimeout(() => {
                    zustandLogout();
                    setAuthTokenForAllServices(null);
                    setTokenReady(false);
                    setProfileLoading(false);

                    auth0Logout({
                        logoutParams: {
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                }, 3000);
            }
        }
    }, [isError, profileError, auth0Logout, navigate, zustandLogout, refetch, setProfileLoading]);

    useEffect(() => {
        const currentPath = window.location.pathname;

        if (profileCompletenessChecked || currentPath === '/update-profile' || !isAuthenticated || !isZustandAuthenticated || !isSuccess || !profileData || !user) {
            return;
        }

        // Mark as checked so this only runs once per session
        setProfileCompletenessChecked(true);

        // Combine backend profile with Auth0 user data for completeness check
        const profileWithEmail = {
            ...profileData,
            email: user.email,
        };

        // Only redirect to update-profile once the user has selected a role.
        // New users without a role see the role selection dialog on the home page first.
        if (!profileData.user_role) {
            return;
        }

        // first_login is the primary signal; fall back to field-completeness
        // when it's false/undefined (e.g. user left profile incomplete previously)
        const needsProfileUpdate = profileData.first_login === true
            ? true
            : !isProfileComplete(profileWithEmail);

        if (needsProfileUpdate) {
            navigate('/update-profile', { replace: true });
        }
    }, [isSuccess, profileData, navigate, isAuthenticated, isZustandAuthenticated, profileCompletenessChecked, user]);

    // Immediately log out if the account is deleted or blocked
    useEffect(() => {
        if (!isSuccess || !profileData) return;

        if (profileData.is_deleted || profileData.is_blocked) {
            performLogoutCleanup();
            auth0Logout({
                logoutParams: {
                    returnTo: window.location.origin,
                    federated: true,
                },
            });
        }
    }, [isSuccess, profileData, auth0Logout]);

    // Single effect to sync Auth0 + Backend profile data to Zustand
    useEffect(() => {
        if (isProfileQueryLoading || isFetching) {
            if (!isProfileLoading) setProfileLoading(true);
            return;
        }

        if (isSuccess && profileData && isAuthenticated && user) {
            const combinedUser = {
                user_id: profileData.user_id,
                uuid: profileData.uuid,
                auth0_id: profileData.auth0_id,
                full_name: profileData.full_name,
                birthday: profileData.birthday,
                phone_number: profileData.phone_number,
                user_role: normalizeRole(profileData.user_role) ?? UserRole.NONE,
                address: profileData.address,
                profile_image: profileData.profile_image,
                negotiateID: profileData.negotiateID,
                kyc_status: profileData.kyc_status,
                card_verified: profileData.card_verified ?? false,
                isSocialLogin: profileData.isSocialLogin ?? false,
                roles: profileData.roles,
                has_admin_portal_access: profileData.has_admin_portal_access ?? false,
                data_policy_accepted: profileData.data_policy_accepted ?? false,
                first_login: profileData.first_login,
                email: user.email,
                picture: user.picture,
                sub: user.sub,
            };

            setZustandUser(combinedUser);

            const roles = user["https://skyporter.us/roles"] || [];
            setUserContext(user.sub || "anonymous", Array.isArray(roles) ? roles : []);
            trackTrace("user_login_success", 1);

            const resolvedUserType = isTravellerRole(profileData.user_role)
              ? UserType.Traveller
              : UserType.Sender;
            const authMethod = profileData.isSocialLogin ? 'google' : 'email';

            if (sessionStorage.getItem('show_signup_notification') === 'true') {
              sessionStorage.removeItem('show_signup_notification');
              SkyporterEvents.signUpCompleted({
                userId: profileData.user_id,
                userType: resolvedUserType,
                method: authMethod,
              });
            } else if (profileData.user_role) {
              SkyporterEvents.login({
                userId: profileData.user_id,
                userType: resolvedUserType,
                method: authMethod,
              });
            }

            if (!roleProcessed) {
                setRoleProcessed(true);
            }

            setProfileLoading(false);
        } else {
            if (!roleProcessed) setRoleProcessed(true);
            if (isProfileLoading) setProfileLoading(false);
        }
    }, [
        profileData,
        isProfileQueryLoading,
        isFetching,
        isSuccess,
        roleProcessed,
        isAuthenticated,
        user,
        setZustandUser,
    ]);

    // Request location permission after successful authentication
    useEffect(() => {
        let mounted = true;

        const requestLocation = async () => {
            // Only request if user is authenticated and profile is loaded
            if (!isAuthenticated || !profileData || isProfileLoading) {
                return;
            }

            try {
                const coordinates = await requestLocationPermission();

                if (mounted) {
                    saveCoordinates(coordinates);
                }
            } catch {
                // No need to show error to user as we have fallback
            }
        };

        requestLocation();

        return () => {
            mounted = false;
        };
    }, [isAuthenticated, profileData, isProfileLoading]);

    if (error) {
        trackException(error, "AuthInitializer");

        const isPopupClosed = error.message?.toLowerCase().includes("popup closed") ||
            error.message?.toLowerCase().includes("popup_closed");

        if (isPopupClosed) {
            window.location.reload();
            return <FullPageSkeleton />;
        }

        return (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h1>{t('authError.title')}</h1>
                <p>{error.message}</p>
            </div>
        );
    }

    if (
        isInitializing ||
        (isAuthenticated && (!tokenReady || isProfileQueryLoading || isFetching))
    ) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    padding: '24px',
                    gap: '16px',
                }}
            >
                <Skeleton variant="rectangular" width="100%" height="60px" sx={{ maxWidth: '800px', borderRadius: '8px' }} />
                <Skeleton variant="rectangular" width="100%" height="400px" sx={{ maxWidth: '800px', borderRadius: '8px' }} />
            </Box>
        );
    }

    return (
        <>
            {children}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AuthInitializer;